import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Container, Row, Col, Card, Form, Button, Alert } from 'react-bootstrap';
import BannerBackground from '@/components/common/BannerBackground';
import logoDark from '@/assets/images/logo-dark.png';
import { apiService } from '@/services/api';

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: ''
  });

  const [validationErrors, setValidationErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [tokenValid, setTokenValid] = useState(true);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Extract token, email, and mode flags from URL parameters
  const urlParams = new URLSearchParams(location.search);
  const token = urlParams.get('token');
  const email = urlParams.get('email');
  const isWelcome = urlParams.get('welcome') === 'true';
  const isMustChange = urlParams.get('mustChange') === 'true';

  // Determine the page mode: 'welcome', 'mustChange', or 'reset'
  const getPageMode = () => {
    if (isWelcome) return 'welcome';
    if (isMustChange) return 'mustChange';
    return 'reset';
  };
  const pageMode = getPageMode();

  // Dynamic text based on the page mode (three-mode system)
  const pageText = {
    welcome: {
      title: 'Set Your Password',
      subtitle: 'Create a password for your new account',
      buttonText: 'Set Password',
      loadingText: 'Setting Password...',
      successTitle: 'Account Setup Complete!',
      successMessage: 'Your password has been set successfully. You can now log in to your account.',
      alertVariant: 'info',
      alertTitle: 'Welcome!',
      alertMessage: 'Your account has been approved. Please create a password to get started.',
      expiryNote: 'This setup link expires after 24 hours for security.',
      accountLabel: 'Your Account'
    },
    mustChange: {
      title: 'Change Your Password',
      subtitle: 'Your password has expired and must be changed',
      buttonText: 'Update Password',
      loadingText: 'Updating Password...',
      successTitle: 'Password Updated!',
      successMessage: 'Your password has been updated successfully. You can now log in with your new password.',
      alertVariant: 'warning',
      alertTitle: 'Password Change Required',
      alertMessage: 'For security reasons, you must update your password before continuing.',
      expiryNote: 'Please complete this form to continue using your account.',
      accountLabel: 'Account'
    },
    reset: {
      title: 'Reset Your Password',
      subtitle: 'Enter your new password below',
      buttonText: 'Reset Password',
      loadingText: 'Resetting Password...',
      successTitle: 'Password Reset Successful!',
      successMessage: 'Your password has been reset successfully. You can now log in with your new password.',
      alertVariant: null,
      alertTitle: null,
      alertMessage: null,
      expiryNote: 'Password reset links expire after 24 hours for security.',
      accountLabel: 'Account'
    }
  }[pageMode];

  useEffect(() => {
    if (!token || !email) {
      setTokenValid(false);
    }
  }, [token, email]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }

    if (submitError) {
      setSubmitError('');
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.newPassword) {
      errors.newPassword = pageMode === 'reset' ? 'New password is required' : 'Password is required';
    } else if (formData.newPassword.length < 6) {
      errors.newPassword = 'Password must be at least 6 characters';
    } else if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.newPassword)) {
      errors.newPassword = 'Password must contain at least one lowercase letter, one uppercase letter, and one number';
    }

    if (!formData.confirmPassword) {
      errors.confirmPassword = 'Please confirm your password';
    } else if (formData.newPassword !== formData.confirmPassword) {
      errors.confirmPassword = 'The passwords do not match';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setSubmitError('');

    try {
      // KAN-182: route through apiService (env base URL) instead of a hardcoded fetch.
      const data = await apiService.post('/storefront/reset-password', {
        email: email,
        token: token,
        newPassword: formData.newPassword,
        confirmPassword: formData.confirmPassword,
        mustChangePassword: isMustChange ? 1 : 0
      });

      if (data.status === 'Success' || data.Status === 'Success') {
        setSuccessMessage(data.message || data.Message || pageText.successMessage);
        setIsSuccess(true);
      } else {
        setSubmitError(data.message || data.Message || 'Failed to reset password. Please try again.');
      }
    } catch (error) {
      console.error('Password reset error:', error);
      // apiService rejects on non-2xx with a transformed error ({ message, errors }).
      let errorMessage;
      if (pageMode === 'welcome') {
        errorMessage = 'Failed to set password. Please try again.';
      } else if (pageMode === 'mustChange') {
        errorMessage = 'Failed to update password. Please try again.';
      } else {
        errorMessage = 'Failed to reset password. Please try again.';
      }

      if (error?.message) {
        errorMessage = error.message;
      } else if (error?.errors && error.errors.length > 0) {
        errorMessage = error.errors.join(' ');
      }

      setSubmitError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleLoginRedirect = () => {
    navigate('/login');
  };

  if (isSuccess) {
    return (
      <div className="min-vh-100 d-flex align-items-center bg-light">
        <BannerBackground/>
        <Container className="position-relative">
          <Row className="justify-content-center">
            <Col md={6} lg={5} xl={4}>
              <Card className="shadow border-0">
                <Card.Body className="p-5">
                  <div className="text-center mb-4">
                    <img 
                      src={logoDark}
                      alt="Stash House"
                      className="stash-header__logo-img img-fluid mb-3"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextElementSibling.style.display = 'inline';
                      }}
                  />
                  </div>

                  <div className="text-center mb-4">
                    <div 
                      className="d-inline-flex align-items-center justify-content-center rounded-circle bg-success bg-opacity-10 mb-3"
                      style={{ width: '64px', height: '64px' }}
                    >
                      <svg 
                        width="32" 
                        height="32" 
                        viewBox="0 0 24 24" 
                        fill="none" 
                        xmlns="http://www.w3.org/2000/svg"
                        className="text-success"
                      >
                        <path 
                          d="M20 6L9 17L4 12" 
                          stroke="currentColor" 
                          strokeWidth="2" 
                          strokeLinecap="round" 
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                    <h4 className="fw-bold text-success mb-2">{pageText.successTitle}</h4>
                  </div>

                  <Alert variant="success" className="mb-4">
                    {successMessage}
                  </Alert>

                  <p className="text-muted text-center mb-4">
                    {pageText.successMessage}
                  </p>

                  <div className="d-grid">
                    <Button
                      variant="primary"
                      size="lg"
                      onClick={handleLoginRedirect}
                    >
                      Continue to Login
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div className="min-vh-100 d-flex align-items-center bg-light">
        <Container className="position-relative">
          <Row className="justify-content-center">
            <Col md={6} lg={5} xl={4}>
              <Card className="shadow border-0">
                <Card.Body className="p-5">
                  <div className="text-center mb-4">
                    <img 
                      src={logoDark}
                      alt="Stash House"
                      className="stash-header__logo-img img-fluid mb-3"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextElementSibling.style.display = 'inline';
                      }}
                  />
                  </div>

                  <div className="text-center mb-4">
                    <div 
                      className="d-inline-flex align-items-center justify-content-center rounded-circle bg-danger bg-opacity-10 mb-3"
                      style={{ width: '64px', height: '64px' }}
                    >
                      <svg 
                        width="32" 
                        height="32" 
                        viewBox="0 0 24 24" 
                        fill="none" 
                        xmlns="http://www.w3.org/2000/svg"
                        className="text-danger"
                      >
                        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                        <path d="M12 8v4M12 16h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                    </div>
                    <h4 className="fw-bold text-danger mb-2">Invalid Link</h4>
                  </div>

                  <Alert variant="danger" className="mb-4">
                    {pageMode === 'welcome'
                      ? 'Invalid setup link. Please contact support or request a new welcome email.'
                      : pageMode === 'mustChange'
                      ? 'Invalid password change link. Please try logging in again or contact support.'
                      : 'Invalid reset link. Please try requesting a new password reset.'}
                  </Alert>

                  <p className="text-muted text-center mb-4">
                    {pageMode === 'welcome'
                      ? 'The account setup link is invalid or has expired. Please contact your administrator to resend the welcome email.'
                      : pageMode === 'mustChange'
                      ? 'The password change link is invalid or has expired. Please try logging in again to receive a new link.'
                      : 'The password reset link is invalid or has expired. Please request a new password reset link.'}
                  </p>

                  <div className="d-grid gap-2">
                    <Button
                      variant="primary"
                      size="lg"
                      onClick={handleLoginRedirect}
                    >
                      Back to Login
                    </Button>
                    {pageMode === 'reset' && (
                      <Link to="/forgot-password" className="btn btn-outline-secondary btn-lg">
                        Request New Reset Link
                      </Link>
                    )}
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  return (
    <div className="min-vh-100 d-flex align-items-center bg-light">
      <BannerBackground/>
      <Container className="position-relative">
        <Row className="justify-content-center">
          <Col md={6} lg={5} xl={4}>
            <Card className="shadow border-0">
              <Card.Body className="p-5">
                <div className="text-center mb-4">
                  <h2 className="fw-bold text-primary mb-1">
                    {import.meta.env.VITE_APP_NAME || 'Wholesale Store'}
                  </h2>
                  <p className="text-muted">{pageText.title}</p>
                </div>

                {pageText.alertVariant && (
                  <Alert variant={pageText.alertVariant} className="mb-4">
                    <strong>{pageText.alertTitle}</strong> {pageText.alertMessage}
                  </Alert>
                )}

                {submitError && (
                  <Alert variant="danger" className="mb-4" dismissible onClose={() => setSubmitError('')}>
                    {submitError}
                  </Alert>
                )}

                <Alert variant="secondary" className="mb-4">
                  <strong>{pageText.accountLabel}:</strong> {email}
                </Alert>

                <Form onSubmit={handleSubmit}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      {pageMode === 'reset' ? 'New Password' : 'Password'} <span className="text-danger">*</span>
                    </Form.Label>
                    <div className="position-relative">
                      <Form.Control
                        type={showNewPassword ? "text" : "password"}
                        name="newPassword"
                        value={formData.newPassword}
                        onChange={handleInputChange}
                        isInvalid={!!validationErrors.newPassword}
                        placeholder={pageMode === 'reset' ? 'Create a new password' : 'Create a password'}
                        disabled={loading}
                        style={{ paddingRight: '2.5rem' }}
                      />
                      <Button
                        variant="link"
                        className="position-absolute top-50 end-0 translate-middle-y text-muted"
                        style={{ zIndex: 10, padding: '0.375rem 0.75rem' }}
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        disabled={loading}
                        type="button"
                        aria-label={showNewPassword ? "Hide password" : "Show password"}
                      >
                        <i className={`bi ${showNewPassword ? 'bi-eye-slash' : 'bi-eye'}`}></i>
                      </Button>
                      <Form.Control.Feedback type="invalid">
                        {validationErrors.newPassword}
                      </Form.Control.Feedback>
                    </div>
                    <Form.Text className="text-muted">
                      Must be at least 6 characters with uppercase, lowercase, and a number
                    </Form.Text>
                  </Form.Group>

                  <Form.Group className="mb-4">
                    <Form.Label>
                      Confirm Password <span className="text-danger">*</span>
                    </Form.Label>
                    <div className="position-relative">
                      <Form.Control
                        type={showConfirmPassword ? "text" : "password"}
                        name="confirmPassword"
                        value={formData.confirmPassword}
                        onChange={handleInputChange}
                        isInvalid={!!validationErrors.confirmPassword}
                        placeholder="Confirm your password"
                        disabled={loading}
                        style={{ paddingRight: '2.5rem' }}
                      />
                      <Button
                        variant="link"
                        className="position-absolute top-50 end-0 translate-middle-y text-muted"
                        style={{ zIndex: 10, padding: '0.375rem 0.75rem' }}
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        disabled={loading}
                        type="button"
                        aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                      >
                        <i className={`bi ${showConfirmPassword ? 'bi-eye-slash' : 'bi-eye'}`}></i>
                      </Button>
                      <Form.Control.Feedback type="invalid">
                        {validationErrors.confirmPassword}
                      </Form.Control.Feedback>
                    </div>
                  </Form.Group>

                  <div className="d-grid gap-2 mb-3">
                    <Button
                      type="submit"
                      variant="primary"
                      size="lg"
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          {pageText.loadingText}
                        </>
                      ) : (
                        pageText.buttonText
                      )}
                    </Button>

                    {pageMode !== 'mustChange' && (
                      <Button
                        type="button"
                        variant="outline-secondary"
                        size="lg"
                        onClick={handleLoginRedirect}
                        disabled={loading}
                      >
                        Back to Login
                      </Button>
                    )}
                  </div>
                </Form>
              </Card.Body>
            </Card>

            <div className="text-center mt-4">
              <small className="text-muted">
                <i className="bi bi-info-circle me-1"></i>
                {pageText.expiryNote}
              </small>
            </div>

            <div className="text-center mt-3">
              <small className="text-muted">
                Need help? Contact your Sale Representative or{' '}
                <Link to="/support" className="text-decoration-none">
                  visit our support page
                </Link>
              </small>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default ResetPasswordPage;